import { FooterButtons, NullableKeys, Option, Options, superUniqueClassName } from '@fl/cmsch-fe-library';
import { isEqual } from 'lodash/fp';
import moment from 'moment';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Opt } from 'ts-opt';
import { UserProfileOrganizationRoleFarmsCenters } from 'api/gen/UserProfileOrganizationRoleFarmsCenters';
import { useOurTranslation } from 'app/translations';
import { formHelpers, useForm } from 'utils/forms';
import { Errors } from 'utils/validator';
import { allOptionValue, allOptionValueArray } from '../../constants/consts';
import { managementOrganizationCenterOptionsSelector, managementOrganizationFarmOptionsSelector, simpleAdminSelector } from '../../model/selector';
import { managementOrganizationFormInitialValues, managementOrganizationFormName, ManagementOrganizationFormValues } from '../../types/management-organization-form-values';
import { UserProfileAndRole } from '../../types/user-profile-and-role-ids';
import styles from '../global-management-styles.sass';
interface Props {
  /** Selected user and role pair. */
  userProfileAndRole: UserProfileAndRole;
  /** Selected subject id. */
  subjectId: number;
  /** User selector options. */
  userProfileOptions: Options<number>;
  /** Role selector options. */
  roleOptions: Options<number>;
  /** Whether the form is in edit mode. */
  isEdit: boolean;
  /** Existing binding data when editing. */
  dataForEdit: Opt<UserProfileOrganizationRoleFarmsCenters>;
  /** Whether the detail view grants access to all farms. */
  detailHasAccessToAllFarms: boolean;
  /** Whether the detail view grants access to all centers. */
  detailHasAccessToAllCenters: boolean;
  /** Called when the form is submitted. */
  onSubmit(): void;
  /** Called when the form is cancelled. */
  onCancel(): void;
  /** Searches users for the select field. */
  onUserSearch(searchText: string): void;
  /** Searches roles for the select field. */
  onRoleSearch(searchText: string): void;
  /** Triggers synchronous validation refresh. */
  onSyncValidate(): void;
  /** Validation function for the form. */
  validate(values: ManagementOrganizationFormValues): Errors<ManagementOrganizationFormValues>;
  /** Clears stored form values. */
  clearFormValues(): void;
}

// eslint-disable-next-line max-lines-per-function
const ManagementOrganizationFormBase: FC<Props> = ({
  userProfileAndRole: {
    userProfile,
    role
  },
  userProfileOptions,
  roleOptions,
  subjectId,
  isEdit,
  dataForEdit,
  onSubmit,
  onCancel,
  onUserSearch,
  onRoleSearch,
  onSyncValidate,
  validate,
  clearFormValues,
  detailHasAccessToAllCenters,
  detailHasAccessToAllFarms
}) => {
  const {
    t,
    tCommon
  } = useOurTranslation('admin');
  const {
    id: userProfileId,
    name: userProfileName
  } = userProfile ?? {
    id: null,
    name: ''
  };
  const {
    id: roleId,
    name: roleName
  } = role ?? {
    id: null,
    name: ''
  };
  const formValues = useSelector(formHelpers.formValues(managementOrganizationFormName));
  const farmIds = formValues.prop('farmIds').orUndef();
  const centerIds = formValues.prop('organizationCenterIds').orUndef();
  const loading = useSelector(simpleAdminSelector.loading);
  const allCentersOption: Option<number> | undefined = useMemo(() => ({
    value: allOptionValue,
    label: t('allOrganizations')
  }), [t]);
  const allFarmsOption: Option<number> | undefined = useMemo(() => ({
    value: allOptionValue,
    label: t('allFarms')
  }), [t]);
  const initialValues: NullableKeys<ManagementOrganizationFormValues> = useMemo(() => {
    if (isEdit && dataForEdit.isFull) {
      return {
        approvedFrom: dataForEdit.propOrCrash('approvedFrom'),
        approvedTo: dataForEdit.prop('approvedTo').orNull(),
        subjectId,
        farmIds: detailHasAccessToAllFarms ? allOptionValueArray : dataForEdit.prop('farms').mapIn(f => f.id).orNull(),
        organizationId: dataForEdit.prop('organization').prop('id').orNull(),
        organizationCenterIds: detailHasAccessToAllCenters ? allOptionValueArray : dataForEdit.prop('organizationCenters').mapIn(o => o.id).orNull(),
        roleIds: dataForEdit.prop('role').prop('id').toArray(),
        userProfileId: dataForEdit.prop('user').prop('userProfileId').orNull(),
        userProfileOrganizationRoleId: dataForEdit.prop('userProfileOrganizationRoleId').orNull(),
        tag: 'ValidateUpdateOrganizationBindingManager'
      };
    } else {
      return managementOrganizationFormInitialValues(userProfileId, roleId, subjectId);
    }
  }, [isEdit, dataForEdit, subjectId, detailHasAccessToAllFarms, detailHasAccessToAllCenters, userProfileId, roleId]);
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating,
    change
  } = useForm({
    form: managementOrganizationFormName,
    initialValues,
    destroyOnUnmount: true,
    onSubmit,
    validate
  });
  const approvedToRef = useRef<HTMLInputElement>(null);
  const approveFrom = useSelector(formHelpers.formValues(managementOrganizationFormName)).prop('approvedFrom').orUndef();
  const centerOptions = useSelector(managementOrganizationCenterOptionsSelector);
  const farmOptions = useSelector(managementOrganizationFarmOptionsSelector);
  const handleCenterIdsChange = useCallback((newCenterIdsOpt: Opt<Array<number>>) => {
    const newCenterIds = newCenterIdsOpt.orElse([]);
    const allFarmsSelected = isEqual(farmIds, allOptionValueArray);
    const allOptionsSelected = newCenterIds.length === centerOptions.length;
    const allCentersSelected = isEqual(newCenterIds, allOptionValueArray) || allOptionsSelected;
    if (allCentersSelected) {
      change('organizationCenterIds', allOptionValueArray);
      change('farmIds', allOptionValueArray);
    } else if (allFarmsSelected) {
      change('farmIds', null);
    }
    onSyncValidate();
  }, [onSyncValidate, farmIds, centerOptions, change]);
  const handleFarmIdsChange = useCallback((newFarmIdsOpt: Opt<Array<number>>) => {
    const newFarmIds = newFarmIdsOpt.orElse([]);
    const allCentersSelected = isEqual(centerIds, allOptionValueArray);
    const allOptionsSelected = newFarmIds.length === farmOptions.length;
    const allFarmsSelected = isEqual(newFarmIds, allOptionValueArray) || allOptionsSelected;
    if (allFarmsSelected) {
      change('organizationCenterIds', allOptionValueArray);
      change('farmIds', allOptionValueArray);
    } else if (allCentersSelected) {
      change('organizationCenterIds', null);
    }
    onSyncValidate();
  }, [onSyncValidate, centerIds, farmOptions, change]);
  const approveFromMoment = useMemo(() => moment(approveFrom), [approveFrom]);
  const handleApproveFromChange = useCallback((value: Opt<string>) => {
    onSyncValidate();
    value.onSome(date => {
      const [element] = document.getElementsByClassName(superUniqueClassName);
      element?.classList.remove('ant-picker-focused');
      setTimeout(() => approvedToRef.current?.focus());
      change('approvedFrom', date);
    });
  }, [onSyncValidate, approvedToRef, change]);
  useEffect(() => {
    if (!isEdit) {
      onUserSearch(userProfileName);
      onRoleSearch(roleName);
    }
  }, [onUserSearch, onRoleSearch, roleName, userProfileName, isEdit]);
  useEffect(() => () => {
    clearFormValues();
  }, [clearFormValues]);
  return <Form data-sentry-element="Form" data-sentry-component="ManagementOrganizationFormBase" data-sentry-source-file="index.tsx">
            <div className={styles.formContainer}>
                <div className={styles.doubleWidth}>
                    <Fields.NumberSelect name="userProfileId" label={t('loginName')} options={userProfileOptions} disabled={submitting || isEdit || Boolean(userProfileId)} onSearch={onUserSearch} onFieldChange={onSyncValidate} fieldIsLoading={loading.includes('getUsers')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="organizationCenterIds" label={t('organizations')} options={centerOptions} disabled={submitting} selectAllOption={allCentersOption} onFieldChange={handleCenterIdsChange} isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="farmIds" label={t('farms')} options={farmOptions} disabled={submitting} selectAllOption={allFarmsOption} onFieldChange={handleFarmIdsChange} isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="roleIds" label={t('roles')} options={roleOptions} disabled={submitting || isEdit || Boolean(roleId)} onFieldChange={onSyncValidate} onSearch={onRoleSearch} fieldIsLoading={loading.includes('getRoles')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedFrom" label={t('approvedFrom')} onFieldChange={handleApproveFromChange} isRequired clearable dontIncludePast disabled={isEdit} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedTo" label={t('approvedTo')} onFieldChange={onSyncValidate} clearable dontIncludePast inputRef={approvedToRef} minDate={approveFromMoment} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
            </div>
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={isEdit ? tCommon('save') : tCommon('add')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Organization management form.
 *
 * @example
 * <ManagementOrganizationForm
 *   userProfileAndRole={userProfileAndRole}
 *   subjectId={123}
 *   userProfileOptions={userProfileOptions}
 *   roleOptions={roleOptions}
 *   isEdit={false}
 *   dataForEdit={none}
 *   detailHasAccessToAllFarms={false}
 *   detailHasAccessToAllCenters={false}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onUserSearch={handleUserSearch}
 *   onRoleSearch={handleRoleSearch}
 *   onSyncValidate={handleSyncValidate}
 *   validate={validate}
 *   clearFormValues={clearFormValues}
 * />
 */
export const ManagementOrganizationForm = memo(ManagementOrganizationFormBase);